import React, { useEffect, useState } from 'react';
import { collection, getDocs, deleteDoc, doc, query, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';
import Swal from 'sweetalert2';

import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';

export default function AdminAgencias() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [agencias, setAgencias] = useState<any[]>([]);
  const [filtro, setFiltro] = useState('');
  const [paginaActual, setPaginaActual] = useState(1);
  const agenciasPorPagina = 10;

  useEffect(() => {
    const fetchAgencias = async () => {
      const snap = await getDocs(collection(db, 'inmobiliaria'));
      const data: any[] = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Ordenar alfabéticamente por nombre
      data.sort((a: any, b: any) => {
        const nombreA = (a.nombre || '').toLowerCase();
        const nombreB = (b.nombre || '').toLowerCase();
        return nombreA.localeCompare(nombreB);
      });
      setAgencias(data);
    };
    fetchAgencias();
  }, []);

  // 🔐 Validación de acceso
  if (!user || !user.tipo?.includes('admin')) {
    return (
      <div className="container mt-5">
        <h2>No tienes permiso para acceder a esta sección</h2>
      </div>
    );
  }

  const handleDelete = async (docId: string) => {
    const confirm = await Swal.fire({
      title: '¿Eliminar agencia?',
      text: 'Se eliminarán sus afiliados, asociados, propiedades y proyectos',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#d33'
    });

    if (!confirm.isConfirmed) return;

    try {
      // ✅ Usar doc.id para buscar relaciones (inmobiliaria_doc_id)
      const afiliadosSnap = await getDocs(query(collection(db, 'asesor'), where('inmobiliaria_doc_id', '==', docId)));
      for (const docu of afiliadosSnap.docs) {
        await deleteDoc(doc(db, 'asesor', docu.id));
      }

      const asociadosSnap = await getDocs(query(collection(db, 'asociado'), where('inmobiliaria_doc_id', '==', docId)));
      for (const docu of asociadosSnap.docs) {
        await deleteDoc(doc(db, 'asociado', docu.id));
      }

      const propSnap = await getDocs(query(collection(db, 'propiedades'), where('inmobiliaria_doc_id', '==', docId)));
      for (const docu of propSnap.docs) {
        await deleteDoc(doc(db, 'propiedades', docu.id));
      }

      const projSnap = await getDocs(query(collection(db, 'proyectos'), where('inmobiliaria_doc_id', '==', docId)));
      for (const docu of projSnap.docs) {
        await deleteDoc(doc(db, 'proyectos', docu.id));
      }

      await deleteDoc(doc(db, 'inmobiliaria', docId));
      setAgencias(prev => prev.filter(a => a.id !== docId));

      Swal.fire({
        icon: 'success',
        title: 'Agencia eliminada',
        text: 'La agencia y sus elementos han sido eliminados.',
        confirmButtonColor: '#3085d6'
      });
    } catch (error) {
      console.error('Error al eliminar agencia:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un problema al eliminar la agencia.',
        confirmButtonColor: '#d33'
      });
    }
  };

  // 🔎 Filtro por nombre
  const agenciasFiltradas = agencias.filter(a =>
    a.nombre?.toLowerCase().includes(filtro.toLowerCase())
  );

  // 📄 Paginación
  const totalPaginas = Math.ceil(agenciasFiltradas.length / agenciasPorPagina);
  const startIndex = (paginaActual - 1) * agenciasPorPagina;
  const agenciasPaginadas = agenciasFiltradas.slice(startIndex, startIndex + agenciasPorPagina);

  return (
    <>
      <style>{`
        .search-input::placeholder {
          color: #6c757d;
          opacity: 1;
        }
      `}</style>
      <UserNav />
      <div className="container mt-5">
        <Breadcrumb
          items={[
            { label: 'Dashboard', path: '/admin-dashboard' },
            { label: 'Administración de Agencias' }
          ]}
        />
        <h1 className="text-center mb-4">Administración de Agencias</h1>

        <div className="d-flex justify-content-between mb-3">
          <input
            type="text"
            className="form-control me-3 search-input"
            style={{ maxWidth: '300px' }}
            placeholder="Buscar por nombre..."
            value={filtro}
            onChange={(e) => {
              setFiltro(e.target.value);
              setPaginaActual(1); // reiniciar página al buscar
            }}
          />
          <button className="btn btn-success" onClick={() => navigate('/add-inm')}>
            + Agregar Inmobiliaria
          </button>
        </div>

        {agenciasFiltradas.length === 0 ? (
          <p>No hay agencias registradas.</p>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>Nombre</th>
                    <th>Correo</th>
                    <th>Teléfono</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {agenciasPaginadas.map(agencia => (
                    <tr key={agencia.id}>
                      <td>{agencia.nombre}</td>
                      <td>{agencia.correo}</td>
                      <td>{agencia.telefono}</td>
                      <td className="text-center">
                        <div className="dropdown">
                          <button
                            className="btn btn-sm btn-outline-secondary dropdown-toggle"
                            type="button"
                            id={`dropdown-${agencia.id}`}
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            <i className="fa-solid fa-gear"></i>
                          </button>
                          <ul className="dropdown-menu" aria-labelledby={`dropdown-${agencia.id}`}>
                            <li>
                              <button
                                className="dropdown-item"
                                onClick={() => navigate(`/ver-inm/${agencia.id}`)}
                              >
                                <i className="fa-solid fa-eye me-2"></i>Ver Detalles
                              </button>
                            </li>
                            <li>
                              <button
                                className="dropdown-item"
                                onClick={() => navigate(`/edit-inmobiliaria/${agencia.id}`)}
                              >
                                <i className="fa-solid fa-pen-to-square me-2"></i>Editar
                              </button>
                            </li>
                            <li><hr className="dropdown-divider" /></li>
                            <li>
                              <button
                                className="dropdown-item text-danger"
                                onClick={() => handleDelete(agencia.id)}
                              >
                                <i className="fa-solid fa-trash me-2"></i>Eliminar
                              </button>
                            </li>
                          </ul>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            <nav className="d-flex justify-content-center mt-3">
              <ul className="pagination">
                {Array.from({ length: totalPaginas }, (_, i) => (
                  <li
                    key={i}
                    className={`page-item ${paginaActual === i + 1 ? 'active' : ''}`}
                    onClick={() => setPaginaActual(i + 1)}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="page-link">{i + 1}</span>
                  </li>
                ))}
              </ul>
            </nav>
          </>
        )}
      </div>
      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
